import { CaseTitle } from "@/features/case/components/CaseTitle";
import { CaseStep } from "@/features/case/components/CaseStep";
import { CaseBasicInfo } from "@/features/case/components/CaseBasicInfo/CaseBasicInfo";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Stack } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { TCaseBasicInfo } from "@/features/case/types/case";
import { CaseMatches } from "@/features/case/components/CaseMatches/CaseMatches";
import { CASE_CREATE_TALENT_STORAGE_KEY } from "@/features/case/constants/storage-keys";
import { Form } from "@/components/elements/Form";
import { IconPlus } from "@/assets/icons";
import { DuplicateCaseModal } from "@/features/case/components/case-common/DuplicateCaseModal";
import { CaseMemoField } from "@/features/case/components/case-common/CaseMemoField";
import { TCheckCaseDuplicateDto } from "@/api/services/main/case/dtos/check-case-duplicate.dto";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import { Control, UseFormReturn } from "react-hook-form";
import { ObjectSchema } from "yup";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";

type CaseCreateFormLayoutProps = {
  // Form methods and state
  methods: UseFormReturn<TCaseBasicInfo>;
  control: Control<TCaseBasicInfo>;
  caseSchema: ObjectSchema<TCaseBasicInfo>;
  onSubmit: (data: TCaseBasicInfo) => Promise<void>;

  // Job info
  jobId: string | undefined;
  jobInfo: TGetJobDto;
  isLoadingJobInfo: boolean;

  // Case info
  activeStep: number;
  managementNumber: string | undefined;
  caseName: string | undefined;

  // Duplicate case modal
  duplicateCaseModalOpen: boolean;
  duplicateCases: TCheckCaseDuplicateDto[];
  pendingCaseName: string;
  onDuplicateCaseModalClose: () => void;

  // Navigation
  onNavigateBack: () => void;
};

export const CaseCreateFormLayout = ({
  methods,
  control,
  caseSchema,
  onSubmit,
  jobId,
  jobInfo,
  isLoadingJobInfo,
  activeStep,
  managementNumber,
  caseName,
  duplicateCaseModalOpen,
  duplicateCases,
  pendingCaseName,
  onDuplicateCaseModalClose,
  onNavigateBack,
}: CaseCreateFormLayoutProps) => {
  const route = jobId ? getDynamicRoute(AppRoutes.caseCreate, { jobId: jobId }) : undefined;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={caseSchema}>
      <CaseTitle jobInfo={jobInfo} managementNumber={managementNumber} caseName={caseName} hideShareButton />
      <CaseStep activeStep={activeStep} />
      <CaseBasicInfo jobInfo={jobInfo} />
      <CaseMatches
        companySpecificIndustryId={jobInfo.companySpecificIndustryId ?? undefined}
        jobId={Number(jobId) ?? undefined}
        isJobInfoLoading={isLoadingJobInfo}
        pageValidationBasePath={route}
        storageKeyPrefix="case_create"
        talentStorageKey={CASE_CREATE_TALENT_STORAGE_KEY}
      />
      <CaseMemoField control={control} />
      <FooterContainer>
        <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap={2}>
            <Button variant="ghost" size="sm" onClick={onNavigateBack}>
              戻る
            </Button>
          </Stack>
          <Stack direction="row" gap={3}>
            <Button type="submit" variant="primary" size="sm" startIcon={<IconPlus width={20} height={20} />}>
              案件を作成
            </Button>
          </Stack>
        </Stack>
      </FooterContainer>
      <DuplicateCaseModal
        open={duplicateCaseModalOpen}
        onClose={onDuplicateCaseModalClose}
        duplicateCases={duplicateCases}
        caseName={pendingCaseName}
        jobId={Number(jobId)}
      />
    </Form>
  );
};
